/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Image } from '@fluentui/react/lib/Image';
import { Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { LuisText } from 'src/core/components/LuisText';
import { Page } from 'src/core/components/Page';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import {
    logLandingPageBottomReached,
    logLanguageResourceNotFound,
    logMicrosoftSubscriptionNotFound
} from 'src/core/services/telemetry/eventsNamesTemplate';
import { getLogger } from 'src/core/services/telemetry/telemetryLogger';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { getAssetsUrl } from 'src/core/utils/api/getAssetsUrl';
import { CustomProjectsSection } from 'src/languageStudio/appShell/landingPage/components/CustomProjectsSection';
import { DemoTileSection } from 'src/languageStudio/appShell/landingPage/components/DemoTileSection';
import { LanguageStudioSubHeader } from 'src/languageStudio/appShell/landingPage/components/LanguageStudioSubHeader';
import { LearningSection } from 'src/languageStudio/appShell/landingPage/components/LearningSection';
import { pageClass } from 'src/languageStudio/appShell/prebuiltDemos/utils/commonStyles';
import styled from 'styled-components';

const messages = defineMessages({
    azureIconAlt: { id: 'languageStudio.languageStudioLandingPage.azureIconAlt', defaultMessage: 'Azure icon' },
    title: { id: 'languageStudio.languageStudioLandingPage.title', defaultMessage: 'Welcome to Language Studio' },
    tabTitle: { id: 'languageStudio.languageStudioLandingPage.tabTitle', defaultMessage: 'Language Studio - Microsoft Azure' },
    backgroundImageAlt: { id: 'languageStudio.languageStudioLandingPage.backgroundImageAlt', defaultMessage: 'Background image' },
    signedOutTitle: {
        id: 'languageStudio.languageStudioLandingPage.signedOutTitle',
        defaultMessage: 'Get started with Azure Cognitive Services for Language'
    },
    signedOutDescription: {
        id: 'languageStudio.languageStudioLandingPage.signedOutDescription',
        defaultMessage: `Use our Natural Language Processing (NLP) features to analyze your textual data using state-of-the-art
    pre-configured AI models or customize your own models to fit your scenario.`
    }
});

const HeaderImage = styled.img({
    top: 0,
    right: 0,
    bottom: 0,
    zIndex: -1,
    height: '100%',
    opacity: '0.8',
    position: 'absolute'
});

const theme = getScopedTheme('LanguageStudioLandingPage');
const SignedOutHeaderContainer = styled(Stack)({
    paddingBottom: '40px',
    borderBottom: `1px solid ${theme.signedOutHeaderBorderColor}`
});

/**
 * Displaying the intro page of the Language Studio portal.
 */
export const LanguageStudioLandingPage = observer(() => {
    const { rootStore } = useGlobalState();
    const { localize } = useLocalization();
    const { isSignedIn } = rootStore.authStore;
    const { isXLargeOrGreater } = useResponsiveMode();

    const subscriptions = rootStore.azureStore.subscriptionStoreHouse.items;
    const subscriptionsTracker = rootStore.azureStore.subscriptionStoreHouse.tracker;
    const resources = rootStore.azureStore.resourceGroupStoreHouse.items;
    const resourcesTracker = rootStore.azureStore.resourceGroupStoreHouse.tracker;

    if (subscriptionsTracker.isCompleted && !subscriptions.length) {
        logMicrosoftSubscriptionNotFound();
    }

    if (resourcesTracker.isCompleted && !resources.length) {
        logLanguageResourceNotFound();
    }

    const handleScroll = (e: any) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;

        if (clientHeight !== scrollHeight && Math.round(scrollTop + clientHeight) >= scrollHeight) {
            logLandingPageBottomReached();
        }
    };

    React.useEffect(() => {
        getLogger('LanguageStudioLandingPage').logEvent('LanguageStudioLandingPageUrlReferrer', { urlReferrer: document.referrer });
    }, []);

    return (
        <>
            <LanguageStudioSubHeader marginLeft="9.5%" />

            <Page
                onScroll={handleScroll}
                size="container"
                className={pageClass}
                title={localize(messages.tabTitle)}
                pageName="LanguageStudioLandingPage">
                <Stack tokens={{ childrenGap: 20 }}>
                    {!isSignedIn && (
                        <SignedOutHeaderContainer styles={{ root: { position: 'relative' } }}>
                            {isXLargeOrGreater && (
                                <HeaderImage
                                    alt={localize(messages.backgroundImageAlt)}
                                    src={getAssetsUrl('assets/images/languageStudio/signedOutBackground.svg')}
                                />
                            )}
                            <Image alt={localize(messages.azureIconAlt)} src={getAssetsUrl('assets/images/languageStudio/azureIcon.svg')} />
                            <Stack tokens={{ maxWidth: 500 }}>
                                <LuisText variant="xxLargePlus" as="h1" fontWeight={600}>
                                    {localize(messages.signedOutTitle)}
                                </LuisText>
                                <LuisText variant="large">{localize(messages.signedOutDescription)}</LuisText>
                            </Stack>
                        </SignedOutHeaderContainer>
                    )}
                    {isSignedIn && [
                        <EditableTitle titleTextProps={{ fontWeight: 600 }} title={localize(messages.title)} />,
                        <CustomProjectsSection />
                    ]}
                    <DemoTileSection />
                    <LearningSection />
                </Stack>
            </Page>
        </>
    );
});
